import classNames from 'classnames';
import React from 'react';
import { Nullable } from '../Shared/Types';
import { orange, primaryLight, red, successGlowText, white } from '../Styles/Colours';
import styles from './AlertBanner.module.scss';
import { Button, ButtonMode } from './Button';
import { AlertIcon, CloseIcon, ConfirmedIcon, InfoIcon } from './Icons';

export enum AlertBannerTypes {
    Prompt,
    Warn,
    Error,
    Success
}

interface Props {
    invertColours?: boolean;
    promptMessage?: React.ReactNode;
    promptExtraInfo?: React.ReactNode;
    infoLine?: React.ReactNode;
    promptType?: AlertBannerTypes;
    multiline?: boolean; // will truncate to one line if this is not enabled
    classNames?: string;
    iconImage?: string;
    verticalSpace?: boolean;
    onDismissAlert?(value: Nullable<any>): void;
}

export function AlertBanner(props: Props) {
    const { promptType, promptMessage, infoLine, onDismissAlert, multiline, promptExtraInfo } = props;

    const containerClasses = classNames(styles.alertBase, props.classNames, {
        [styles.verticalSpace]: props.verticalSpace,
        [styles.invert]: props.invertColours,
        [styles.alertPrompt]: promptType === AlertBannerTypes.Prompt,
        [styles.alertWarn]: promptType === AlertBannerTypes.Warn,
        [styles.alertError]: promptType === AlertBannerTypes.Error,
        [styles.alertSuccess]: promptType === AlertBannerTypes.Success,
        [styles.multiline]: multiline || !!infoLine
    });

    const iconContainer = classNames(styles.alertIconContainer, {
        [styles.multiline]: multiline,
        [styles.iconImage]: !!props.iconImage
    });

    const textContainer = classNames(styles.textContainer, {
        [styles.multiline]: multiline
    });

    let icon: JSX.Element;

    if (!!props.iconImage) {
        icon = <img src={props.iconImage} alt='' className={styles.iconImage} />;
    } else if (promptType === AlertBannerTypes.Warn) {
        icon = <AlertIcon size='20' color={props.invertColours ? white : orange} />;
    } else if (promptType === AlertBannerTypes.Error) {
        icon = <AlertIcon size='20' color={props.invertColours ? white : red} />;
    } else if (promptType === AlertBannerTypes.Success) {
        icon = <ConfirmedIcon size='20' color={props.invertColours ? white : successGlowText} />;
    } else {
        icon = <InfoIcon size='20' color={props.invertColours ? white : primaryLight} />;
    }

    return (
        <div className={containerClasses}>
            <div className={iconContainer}>{icon}</div>
            <div className={textContainer}>
                <div className={styles.alertMessage}>
                    {promptMessage}
                    {promptExtraInfo && <span className={styles.alertExtraInfo}> {promptExtraInfo}</span>}
                </div>
                {!!infoLine && <div className={styles.info}>{infoLine}</div>}
            </div>
            {onDismissAlert && (
                <Button onClick={onDismissAlert} iconOnly mode={ButtonMode.text} testId='dismiss'>
                    <CloseIcon />
                </Button>
            )}
        </div>
    );
}
